/**
 * Holds the active tenant key as state and drives the react-ui `BrandProvider`.
 *
 * react-auth v4 removed `setBrand`: the react-ui `BrandProvider` is driven by a
 * `tenantKey` string prop. Pathway still resolves the tenant dynamically after
 * authentication (from the signed-in user's email domain), so the key must live
 * in state above the provider and be settable from deep in the tree. This
 * component owns that state, exposes it through `useTenantKey`, and re-renders
 * the brand when the key changes.
 *
 * The runtime override channel is enabled in development only, so a designer
 * can drop a `brand.json` into `public/brands-dev/<folder>/` and restyle a
 * tenant without an npm publish.
 */

import type React from "react";
import type { TenantKey } from "@/config/tenants";

import { BrandProvider } from "@webuild-ai/react-ui/providers";
import { createContext, useContext, useMemo, useState } from "react";

import { tenantPacks } from "@/config/tenants";

interface TenantKeyContextValue {
  /** The active tenant key, matching a react-ui pack `key`. */
  tenantKey: TenantKey;
  /** Switch the active tenant, e.g. after resolving the signed-in user. */
  setTenantKey: (key: TenantKey) => void;
}

const TenantKeyContext = createContext<TenantKeyContextValue | null>(null);

export interface TenantProviderProps {
  /** Initial tenant key, resolved from the auth config at startup. */
  initialTenantKey: TenantKey;
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ initialTenantKey, children }) => {
  const [tenantKey, setTenantKey] = useState<TenantKey>(initialTenantKey);

  const value = useMemo<TenantKeyContextValue>(() => ({ tenantKey, setTenantKey }), [tenantKey]);

  return (
    <TenantKeyContext.Provider value={value}>
      <BrandProvider
        enableRuntimeOverride={import.meta.env.DEV}
        tenantKey={tenantKey}
        tenants={tenantPacks}
      >
        {children}
      </BrandProvider>
    </TenantKeyContext.Provider>
  );
};

/** Access and mutate the active tenant key. */
export const useTenantKey = (): TenantKeyContextValue => {
  const context = useContext(TenantKeyContext);

  if (!context) {
    throw new Error("useTenantKey must be used within a TenantProvider");
  }

  return context;
};
